import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo10Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 10 BWV 781
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV781/bach-invention-10/bach-invention-10-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 10 BWV 781.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4769">
      <Navbar data-id="element-4770" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4771">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4772">
          <img src="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=400&h=300&fit=crop" alt="Invention No. 10" className="w-full h-64 md:h-80 object-cover" data-id="element-4773" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4774" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4775">
            <div className="max-w-7xl mx-auto" data-id="element-4776">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4777">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4778" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4779">
                Invention No. 10 in G major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4780">BWV 781</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4781">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4782">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4783">
          <div className="flex items-center space-x-2" data-id="element-4784">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4785">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4786" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4787">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4788">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4789" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4790">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4791">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4792" />
              <span className="hidden sm:inline" data-id="element-4793">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4794">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4795" />
              <span className="hidden sm:inline" data-id="element-4796">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4797">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4798">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4799">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 10 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4800" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4801">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4802">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4803">
            <div data-id="element-4804">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4805">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4806">
                <div data-id="element-4807">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4808">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4809">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4810">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4811">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4812">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4813">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4814">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4815">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4816">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4817">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4818">
                    G major
                  </dd>
                </div>
                <div data-id="element-4819">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4820">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4821">
                    BWV 781
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4822">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4823">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4824">
                The tenth of Bach's fifteen Two-Part Inventions, this piece in G
                major is celebrated for its brilliant, virtuosic character and
                sparkling energy. Written as pedagogical works for keyboard
                students, the Inventions teach the art of two-voice counterpoint
                while developing musical ideas through systematic elaboration
                and imitation. This invention features a lively, ascending
                subject that creates an immediate sense of brightness and
                forward motion, with rapid sixteenth-note passages that demand
                both technical precision and musical clarity from the performer.
                The major key gives the work a joyful, celebratory quality that
                makes it one of the most popular and frequently performed
                inventions in the collection. The piece demonstrates Bach's
                mastery of invertible counterpoint, with the two voices engaging
                in a spirited dialogue that maintains perfect balance while
                creating a sense of playful competition. Technical challenges
                include maintaining evenness and clarity in the rapid scalar
                passages, managing the hand crossings that occur when the voices
                intersect, executing clean articulation in the staccato
                sections, and bringing out the contrapuntal lines while
                maintaining the overall sense of brilliance and vitality. This
                invention serves as both an essential technical study in baroque
                keyboard virtuosity and a delightful musical showcase that
                demonstrates Bach's ability to combine pedagogical purpose with
                genuine artistic expression, creating a work that is as
                enjoyable to perform as it is instructive.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4825" />
    </div>;
};